/**
 * Brand detail — everything about one brand, and what is stopping it reaching
 * the Complete stage.
 *
 * The masthead states the brand's position in the lifecycle; the left column
 * carries the three things that can be fixed here (ingestion, catalog quality,
 * assets); the right rail lists the outstanding tasks in worst-first order.
 */

import React, { useState } from 'react';
import { Alert, Box, CircularProgress, TextField } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { brandDirectoryApi, BrandRepInvitation } from '../../services/brandDirectoryApi';
import { brandManagementApi } from '../../services/brandManagementApi';
import { integrationApiService } from '../../services/integrationApi';
import { useToast } from '../../contexts/ToastContext';
import { JOB_TYPE_LABELS } from '../../components/dataIngestion/runFacts';
import {
  bvCardSx,
  bvColor,
  bvContentSx,
  bvRadius,
  bvTransition,
  bvType,
  formatCount,
  tabular,
} from './brandVendorsTokens';
import {
  BrandTask,
  ChipState,
  lifecycleOf,
  STAGE_LABEL,
  StageKey,
  STAGE_STYLE,
  taskSeverity,
  tasksFor,
  brandMetaLine,
  SPEC_MAPPING_LABEL,
  SPEC_MAPPING_NOTE,
  specCoverageNote,
  websiteDomain,
  websiteHref,
} from './brandLifecycle';
import { dealerVerifierSummary, useDealerVerifiers } from './useDealerVerifiers';
import { bvRoutes, taskTarget } from './brandVendorsRoutes';
import {
  BvButton,
  BvCard,
  BvEmptyState,
  BvSwitch,
  DefinitionRows,
  Eyebrow,
  IconArrowOut,
  LogoFrame,
  ProgressBar,
  StagePill,
  StatusDot,
} from './components/BrandVendorsPrimitives';
import { SpecSchemaAdvanced } from './components/SpecSchemaAdvanced';
import { buildBrandCatalogProfile } from '../../utils/brandCatalogProfile';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { FormDialog } from '../../components/ui/FormDialog';
import { ValidatedTextField } from '../../components/ui/ValidatedTextField';
import { validateEmail } from '../../utils/inputValidators';
import { formatFirestoreDateOnly } from '../../utils/firestoreDate';
import { BrandCompanyCard } from './components/BrandCompanyCard';

/** Labels a job type badge (e.g. "price_refresh" → "Price Refresh"); falls back to
 * title-casing anything JOB_TYPE_LABELS doesn't know about yet. */
function formatJobType(type?: string | null): string | null {
  if (!type) return null;
  const known = (JOB_TYPE_LABELS as Record<string, string>)[type];
  if (known) return known;
  return type
    .split('_')
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

const STAGE_ORDER: StageKey[] = ['registered', 'ingesting', 'claimed', 'complete'];

const STAGE_SUBCAPTION: Record<StageKey, string> = {
  registered: 'Record exists',
  ingesting: 'Products flowing',
  claimed: 'Rep on board',
  complete: 'Nothing outstanding',
};

const SOURCE_TYPES: Array<{ label: string; note: string; primary: boolean }> = [
  { label: 'Vendor feed', note: 'The brand pushes a catalog file on a schedule.', primary: true },
  { label: 'Direct API', note: 'We pull from the brand’s B2B endpoint.', primary: false },
  { label: 'Storefront API', note: 'We read the brand’s own web store data.', primary: false },
  { label: 'Brand site scrape', note: 'We render the brand’s product pages in a browser.', primary: false },
  { label: 'Retailer scrape', note: 'We assemble the catalog from retail listings.', primary: false },
];

export const BrandVendorsBrandPage: React.FC = () => {
  const { brandId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['brand-directory-detail', brandId],
    queryFn: () => brandDirectoryApi.get(brandId),
    enabled: Boolean(brandId),
  });

  const runMutation = useMutation({
    mutationFn: () => integrationApiService.triggerIngestion(brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-directory-detail', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-directory'] });
    },
  });

  // Demo brand (MOT-338): kept out of every store and vendor brand list.
  const adminOnlyMutation = useMutation({
    mutationFn: (adminOnly: boolean) => brandManagementApi.updateBrand(brandId, { adminOnly }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-directory-detail', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-directory'] });
    },
  });

  // Sale policy (MOT-388): sale pricing only inside vendor-approved sale windows.
  const saleWindowsMutation = useMutation({
    mutationFn: (saleWindowsRequired: boolean) => brandManagementApi.updateBrand(brandId, { saleWindowsRequired }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-directory-detail', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-directory'] });
    },
  });

  const { lookup: dealerLookup } = useDealerVerifiers();
  const { showToast } = useToast();

  // Brand rep invitations (flag brand_rep_invites): only an unclaimed brand has anyone to invite.
  // The list reports `enabled: false` while the flag is off, and the card then just copies the link.
  const unclaimed = data?.claimedBy.status === 'unclaimed';
  const invitesQuery = useQuery({
    queryKey: ['brand-rep-invites', brandId],
    queryFn: () => brandDirectoryApi.listRepInvites(brandId),
    enabled: Boolean(brandId) && unclaimed,
  });
  const invitesEnabled = Boolean(invitesQuery.data?.enabled);
  const invitations = (invitesQuery.data?.invitations || []).filter((invite) => invite.status !== 'revoked');
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteNote, setInviteNote] = React.useState('');
  const [revoking, setRevoking] = React.useState<BrandRepInvitation | null>(null);

  const refreshInvites = () => queryClient.invalidateQueries({ queryKey: ['brand-rep-invites', brandId] });
  const sendInviteMutation = useMutation({
    mutationFn: () =>
      brandDirectoryApi.sendRepInvite(brandId, { email: inviteEmail.trim(), note: inviteNote.trim() || undefined }),
    onSuccess: (result) => {
      refreshInvites();
      setInviteOpen(false);
      setInviteEmail('');
      setInviteNote('');
      showToast(
        result.emailSent
          ? `Invitation sent to ${result.invitation.email}`
          : 'Invitation saved but the email did not send. Use Resend to try again.',
        result.emailSent ? 'success' : 'warning'
      );
    },
  });
  const resendInviteMutation = useMutation({
    mutationFn: (invite: BrandRepInvitation) => brandDirectoryApi.resendRepInvite(brandId, invite.id),
    onSuccess: (result) => {
      refreshInvites();
      showToast(
        result.emailSent ? `Invitation resent to ${result.invitation.email}` : 'The email did not send. Try again shortly.',
        result.emailSent ? 'success' : 'warning'
      );
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  const revokeInviteMutation = useMutation({
    mutationFn: (invite: BrandRepInvitation) => brandDirectoryApi.revokeRepInvite(brandId, invite.id),
    onSuccess: () => {
      refreshInvites();
      setRevoking(null);
      showToast('Invitation revoked. Its link no longer works.', 'success');
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });


  // An unclaimed brand has no organization to invite into, so "Invite rep" hands
  // out the public vendor signup link, where the rep creates the org and claims
  // the brand. The old behaviour (open the Orgs list) looked like a dead button.
  const openRepInvite = () => {
    if (invitesEnabled) {
      sendInviteMutation.reset();
      setInviteOpen(true);
    } else {
      void copyRepSignupLink();
    }
  };
  const copyRepSignupLink = async () => {
    const link = `${window.location.origin}${bvRoutes.signup}`;
    try {
      await navigator.clipboard.writeText(link);
      showToast('Vendor signup link copied. Send it to the brand’s rep.', 'success');
    } catch {
      showToast(`Copy this link and send it to the rep: ${link}`, 'info');
    }
  };
  const [specMapOpen, setSpecMapOpen] = useState(false);
  const specMapQuery = useQuery({
    queryKey: ['brand-spec-map', brandId],
    queryFn: () => brandDirectoryApi.specMap(brandId),
    enabled: specMapOpen && Boolean(brandId),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: '80px' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (error || !data) {
    return <Alert severity="error" sx={{ m: 3 }}>{(error as Error)?.message || 'Brand not found'}</Alert>;
  }

  const facts = lifecycleOf(data);
  const catalogProfile = buildBrandCatalogProfile(data.id, data.name, data.categories);
  const specCoverage = data.specCoverage || null;
  const dealerRow = dealerLookup ? dealerLookup(data) : undefined;
  const dealer = dealerVerifierSummary(dealerRow);
  const tasks = tasksFor(data, data.dataQualityPercent);
  const severity = taskSeverity(tasks);
  const stageIndex = STAGE_ORDER.indexOf(facts.stage);

  const runTask = (task: BrandTask) => {
    if (task.action.kind === 'invite-rep' && !facts.claimed) {
      openRepInvite();
      return;
    }
    const target = taskTarget(task.action, data.id);
    if (target) {
      navigate(target);
      return;
    }
    // `fix-ingestion` is the only task repaired in place: the source already
    // exists and failed, so re-running it is the actual remedy. Everything else
    // resolves to a destination above.
    if (task.action.kind === 'fix-ingestion') {
      runMutation.mutate();
    }
  };

  const healthDot = !facts.configured ? bvColor.muted2 : facts.failing ? bvColor.danger : bvColor.success;

  return (
    <Box sx={{ minHeight: '100%' }}>
      {/* masthead */}
      <Box sx={{ bgcolor: bvColor.surface, borderBottom: `1px solid ${bvColor.border}`, px: '26px', pt: '24px', pb: '18px' }}>
        <Box sx={bvContentSx}>
          <Box sx={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <LogoFrame
              name={data.name}
              url={data.logoUrl}
              size={104}
              radius={bvRadius.logoFrameLarge}
              borderColor={bvColor.borderWarm}
              artSize={72}
              monogramSize={34}
              elevated
            />
            <Box sx={{ flex: 1, minWidth: 260 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', flexWrap: 'wrap' }}>
                <Box component="h1" sx={{ ...bvType.brandTitle, color: bvColor.ink, m: 0 }}>
                  {data.name}
                </Box>
                <StagePill stage={facts.stage} />
              </Box>
              <Box sx={{ ...bvType.body, color: bvColor.muted, mt: '6px' }}>
                {brandMetaLine(data)}
                {data.claimedBy.organizationName ? ` · claimed by ${data.claimedBy.organizationName}` : ' · unclaimed'}
              </Box>
              {websiteHref(data.website) && (
                <Box
                  component="a"
                  href={websiteHref(data.website) as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'inline-block',
                    mt: '6px',
                    ...bvType.caption,
                    color: bvColor.accent,
                    textDecoration: 'none',
                    '&:hover': { color: bvColor.accentHover, textDecoration: 'underline' },
                  }}
                >
                  {websiteDomain(data.website)}
                </Box>
              )}
              {data.missingLogo && (
                <Box
                  component="button"
                  onClick={() => navigate(bvRoutes.logos(data.id))}
                  sx={{
                    mt: '8px',
                    p: 0,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    color: bvColor.accent,
                    textDecoration: 'underline',
                    ...bvType.caption,
                    '&:hover': { color: bvColor.accentHover },
                  }}
                >
                  Add a logo
                </Box>
              )}
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Box sx={{ display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
                {tasks[0] && (
                  <BvButton size="lg" tone="primary" onClick={() => runTask(tasks[0])} disabled={runMutation.isPending}>
                    {tasks[0].buttonLabel}
                  </BvButton>
                )}
                <BvButton size="lg" endIcon={<IconArrowOut size={13} />} onClick={() => navigate(bvRoutes.dashboard(data.id))}>
                  Brand dashboard
                </BvButton>
              </Box>
              <Box sx={{ ...bvType.caption, color: bvColor.muted, mt: '7px', maxWidth: 300 }}>
                {tasks[0]
                  ? `${tasks[0].label} — the next thing blocking ${STAGE_LABEL[facts.stage] === 'Complete' ? 'this brand' : 'the next stage'}.`
                  : 'Logo, catalog, ingestion and claim are all in place.'}
              </Box>
            </Box>
          </Box>

          {/* stat strip */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              mt: '18px',
              border: `1px solid ${bvColor.borderWarm}`,
              borderRadius: `${bvRadius.panel}px`,
              bgcolor: bvColor.surface,
              overflow: 'hidden',
            }}
          >
            {[
              { label: 'Products', value: formatCount(data.productCount), dot: data.productCount > 0 ? bvColor.success : bvColor.danger },
              { label: 'Data quality', value: `${data.dataQualityPercent}%`, dot: data.dataQualityPercent >= 60 ? bvColor.success : bvColor.warning },
              { label: 'Ingestion', value: facts.configured ? (facts.failing ? 'Failing' : 'Healthy') : 'None', dot: healthDot },
              { label: 'Retail reach', value: `${data.retailReach.carrying}/${data.retailReach.totalStores}`, dot: data.retailReach.carrying > 0 ? bvColor.success : bvColor.muted2 },
              { label: 'Ownership', value: facts.claimed ? 'Claimed' : facts.claimStatus === 'requested' ? 'Requested' : 'Unclaimed', dot: facts.claimed ? bvColor.success : facts.claimStatus === 'requested' ? bvColor.warning : bvColor.danger },
            ].map((cell, index) => (
              <Box
                key={cell.label}
                sx={{
                  flex: '1 1 128px',
                  px: '16px',
                  py: '9px',
                  borderLeft: index === 0 ? 'none' : `1px solid ${bvColor.borderSubtle}`,
                }}
              >
                <Eyebrow>{cell.label}</Eyebrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: '4px' }}>
                  <StatusDot color={cell.dot} />
                  <Box sx={{ fontSize: 14, fontWeight: 600, color: bvColor.ink, ...tabular }}>{cell.value}</Box>
                </Box>
              </Box>
            ))}
          </Box>

          {/* lifecycle track */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', mt: '16px', maxWidth: 820 }}>
            {STAGE_ORDER.map((stageKey, index) => {
              const reached = index <= stageIndex;
              const style = STAGE_STYLE[stageKey];
              return (
                <Box key={stageKey}>
                  <Box
                    sx={{
                      height: reached ? 6 : 3,
                      borderRadius: `${bvRadius.bar}px`,
                      bgcolor: reached ? style.bar : bvColor.border,
                      transition: bvTransition,
                    }}
                  />
                  <Box sx={{ fontSize: 12, fontWeight: reached ? 600 : 500, color: reached ? bvColor.ink : bvColor.muted, mt: '6px' }}>
                    {STAGE_LABEL[stageKey]}
                  </Box>
                  <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '2px' }}>{STAGE_SUBCAPTION[stageKey]}</Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* body */}
      <Box sx={{ px: '26px', pt: '18px', pb: '40px' }}>
        <Box
          sx={{
            ...bvContentSx,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 330px' },
            gap: '16px',
            alignItems: 'start',
          }}
        >
          {/* left column */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
            <BrandCompanyCard key={data.id} brand={data}>
              <DefinitionRows
                labelWidth={108}
                rows={[
                  {
                    label: 'Demo brand',
                    value: (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <BvSwitch
                          checked={Boolean(data.adminOnly)}
                          disabled={adminOnlyMutation.isPending}
                          onChange={(next) => adminOnlyMutation.mutate(next)}
                          label="Demo brand: hide from dealers and vendors"
                        />
                        <Box component="span" sx={{ color: bvColor.muted }}>
                          {data.adminOnly
                            ? 'Hidden from dealers and vendors. Only admins can pick it.'
                            : 'Dealers and vendors can pick this brand.'}
                        </Box>
                      </Box>
                    ),
                  },
                  {
                    label: 'Sale policy',
                    value: (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <BvSwitch
                          checked={Boolean(data.saleWindowsRequired)}
                          disabled={saleWindowsMutation.isPending}
                          onChange={(next) => saleWindowsMutation.mutate(next)}
                          label="Sale pricing only during approved vendor windows"
                        />
                        <Box component="span" sx={{ color: bvColor.muted }}>
                          {data.saleWindowsRequired
                            ? 'Dealers may price below MAP only inside a window the vendor approves. Turning this off ends the sale prices set under its windows and reprices dealers to MAP.'
                            : 'The MAP guard applies as usual; no sale windows are offered for this brand.'}
                        </Box>
                      </Box>
                    ),
                  },
                ]}
              />
              {saleWindowsMutation.isError && (
                <Alert severity="error" sx={{ mx: '13px', mt: '10px' }}>
                  Could not change the sale policy. Try again.
                </Alert>
              )}
              {adminOnlyMutation.isError && (
                <Alert severity="error" sx={{ mx: '13px', mt: '10px' }}>
                  Could not change this setting. Try again.
                </Alert>
              )}
            </BrandCompanyCard>

            <BvCard
              leading={<StatusDot color={healthDot} size={8} />}
              title="Ingestion"
              caption={
                facts.configured
                  ? `${data.ingestion.source}${data.ingestion.paused ? ' · schedule off' : ''}${data.ingestion.lastRunAt ? ` · last run ${new Date(data.ingestion.lastRunAt).toLocaleString('en-US')}` : ''}`
                  : 'No source is pulling this catalog.'
              }
              action={
                facts.configured ? (
                  <BvButton size="sm" onClick={() => runMutation.mutate()} disabled={runMutation.isPending}>
                    {runMutation.isPending ? 'Running…' : 'Run now'}
                  </BvButton>
                ) : undefined
              }
            >
              {facts.configured ? (
                (data.ingestionHistory || []).length > 0 ? (
                  (data.ingestionHistory || []).slice(0, 6).map((run) => {
                    const failed = /fail|error/i.test(run.status);
                    return (
                      <Box
                        key={run.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          px: '16px',
                          py: '10px',
                          borderBottom: `1px solid ${bvColor.borderSubtle}`,
                          '&:last-of-type': { borderBottom: 'none' },
                        }}
                      >
                        <StatusDot color={failed ? bvColor.danger : bvColor.success} size={7} />
                        <Box sx={{ width: 104, ...bvType.caption, color: bvColor.muted, ...tabular }}>
                          {run.completedAt || run.createdAt
                            ? new Date((run.completedAt || run.createdAt) as string).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })
                            : '—'}
                        </Box>
                        <Box sx={{ width: 96, ...bvType.caption, color: failed ? bvColor.danger : bvColor.ink3, fontWeight: 500 }}>
                          {run.status}
                        </Box>
                        <Box sx={{ width: 118, ...bvType.caption, color: bvColor.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {formatJobType(run.type) || '—'}
                        </Box>
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            ...bvType.caption,
                            color: bvColor.muted,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {run.error ||
                            `${formatCount(run.productsProcessed || 0)} processed · ${formatCount(run.productsCreated || 0)} new`}
                        </Box>
                      </Box>
                    );
                  })
                ) : (
                  <BvEmptyState headline="No runs recorded yet." note="The source is configured but has not run." py="32px" />
                )
              ) : (
                <BvEmptyState
                  headline="No ingestion source configured"
                  note="Pick how this brand's catalog reaches us. A vendor feed is the most reliable; a retailer scrape is the fallback when the brand has no feed."
                  // Choosing a source type means persisting a scraperType and a
                  // schedule, which is the ingestion console's job. The console
                  // does not read a source hint yet, so these land on the brand's
                  // connector view and the type is picked there — better than
                  // firing a run against a source that does not exist.
                  action={SOURCE_TYPES.map((type) => (
                    <BvButton
                      key={type.label}
                      tone={type.primary ? 'primary' : 'default'}
                      onClick={() => navigate(bvRoutes.ingestion(data.id))}
                    >
                      {type.label}
                    </BvButton>
                  ))}
                />
              )}
            </BvCard>

            <BvCard
              title="Catalog"
              caption={`${formatCount(data.productCount)} products · ${data.dataQualityPercent}% field completeness`}
            >
              {data.productCount > 0 ? (
                <Box sx={{ px: '16px', py: '13px' }}>
                  {(data.catalogQuality || []).map((metric) => (
                    <Box
                      key={metric.id}
                      sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '8px' }}
                    >
                      <Box sx={{ width: 104, ...bvType.caption, color: bvColor.ink3 }}>{metric.label}</Box>
                      <ProgressBar
                        percent={metric.percent}
                        tone={metric.percent >= 80 ? 'ok' : metric.percent >= 50 ? 'part' : 'no'}
                        sx={{ flex: 1 }}
                      />
                      <Box sx={{ width: 34, textAlign: 'right', ...bvType.caption, color: bvColor.ink, ...tabular }}>
                        {metric.percent}%
                      </Box>
                      <Box sx={{ width: 92, ...bvType.microCaption, color: bvColor.muted }}>{metric.note}</Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <BvEmptyState headline="Nothing ingested yet — configure a source above to seed the catalog." py="40px" />
              )}
            </BvCard>

            <BvCard
              title="Spec schema"
              caption={
                specCoverage?.percent != null
                  ? `${specCoverage.percent}% of spec labels map to canonical specs · ${specCoverageNote(data, specCoverage)}`
                  : specCoverageNote(data, specCoverage)
              }
              action={
                specCoverage && specCoverage.productsWithSpecs > 0 ? (
                  <BvButton size="sm" onClick={() => setSpecMapOpen((open) => !open)} aria-pressed={specMapOpen}>
                    {specMapOpen ? 'Hide map' : 'Advanced'}
                  </BvButton>
                ) : undefined
              }
            >
              {specCoverage && specCoverage.productsWithSpecs > 0 ? (
                <Box sx={{ px: '16px', py: '13px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', py: '6px' }}>
                    <Box sx={{ width: 104, ...bvType.caption, color: bvColor.ink3 }}>Mapped</Box>
                    <ProgressBar
                      percent={specCoverage.percent || 0}
                      tone={(specCoverage.percent || 0) >= 80 ? 'ok' : (specCoverage.percent || 0) >= 50 ? 'part' : 'no'}
                      sx={{ flex: 1 }}
                    />
                    <Box sx={{ width: 34, textAlign: 'right', ...bvType.caption, color: bvColor.ink, ...tabular }}>
                      {specCoverage.percent}%
                    </Box>
                  </Box>
                  <DefinitionRows
                    labelWidth={104}
                    rows={[
                      {
                        label: 'Mapping',
                        value: `${SPEC_MAPPING_LABEL[specCoverage.mappingSource]}${
                          specCoverage.explicitFieldCount ? ` · ${formatCount(specCoverage.explicitFieldCount)} brand rules` : ''
                        }`,
                      },
                      {
                        label: 'Schema',
                        value:
                          specCoverage.productTypes
                            .map((entry) => `${entry.type} (${formatCount(entry.count)})`)
                            .join(' · ') || '—',
                      },
                      {
                        label: 'Sections',
                        value:
                          specCoverage.sections
                            .slice(0, 8)
                            .map((section) => `${section.label} ${formatCount(section.labels)}`)
                            .join(' · ') || 'None — no label reaches a canonical field',
                      },
                      {
                        label: 'Not mapped',
                        value: `${formatCount(specCoverage.catchAllCount)} kept as Other · ${formatCount(
                          specCoverage.unmappedCount
                        )} unmatched`,
                      },
                      ...(specCoverage.topUnmappedLabels.length
                        ? [
                            {
                              label: 'Top misses',
                              value: specCoverage.topUnmappedLabels
                                .map((entry) => `${entry.label} (${formatCount(entry.products)})`)
                                .join(' · '),
                            },
                          ]
                        : []),
                    ]}
                  />
                  <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '8px' }}>
                    {SPEC_MAPPING_NOTE[specCoverage.mappingSource]} Measured on {formatCount(specCoverage.productsWithSpecs)} of{' '}
                    {formatCount(specCoverage.productsSampled)} sampled products.
                  </Box>
                </Box>
              ) : (
                <BvEmptyState
                  headline={specCoverage ? 'No specifications on file for this brand.' : 'Spec coverage could not be read.'}
                  note={SPEC_MAPPING_NOTE[data.specMapping?.source || 'generic']}
                  py="32px"
                />
              )}
              {specMapOpen && (
                <SpecSchemaAdvanced
                  map={specMapQuery.data}
                  loading={specMapQuery.isLoading}
                  error={specMapQuery.error instanceof Error ? specMapQuery.error : null}
                  onRetry={() => {
                    void specMapQuery.refetch();
                  }}
                />
              )}
            </BvCard>

            <BvCard
              title="Dealer check"
              caption={dealerLookup ? `${dealer.label}${dealer.detail ? ` · ${dealer.detail}` : ''}` : 'Loading dealer lists…'}
              action={
                <BvButton size="sm" onClick={() => navigate(bvRoutes.dealerLists)}>
                  Dealer lists
                </BvButton>
              }
            >
              {dealerRow ? (
                <DefinitionRows
                  labelWidth={108}
                  rows={[
                    { label: 'Kind', value: dealer.label },
                    {
                      label: 'Imported list',
                      value: dealerRow.imported
                        ? `${formatCount(dealerRow.imported.count)} dealers${
                            dealerRow.imported.listDate ? ` · list dated ${dealerRow.imported.listDate}` : ''
                          }`
                        : '—',
                    },
                    { label: 'Live lookup', value: dealerRow.claimTime ? 'Checked automatically when a store claims the brand' : '—' },
                    ...(dealerRow.covers.length ? [{ label: 'Also covers', value: dealerRow.covers.join(', ') }] : []),
                    ...(dealerRow.notes ? [{ label: 'Notes', value: dealerRow.notes }] : []),
                  ]}
                />
              ) : (
                <BvEmptyState
                  headline={dealerLookup ? 'No published list or locator.' : 'Loading…'}
                  note="Stores verify this brand with proof of authorization reviewed by an admin."
                  py="32px"
                />
              )}
            </BvCard>

            {catalogProfile && (
              <BvCard
                title="Catalog identity"
                caption={`${catalogProfile.barcodeModelLabel} · ${catalogProfile.confidenceLabel}`}
              >
                <DefinitionRows
                  labelWidth={88}
                  rows={[
                    { label: 'Sells', value: catalogProfile.sellsLabel },
                    { label: 'SKU', value: catalogProfile.skuLooksLike },
                    ...(catalogProfile.skuExample
                      ? [{ label: 'Example', value: catalogProfile.skuExample }]
                      : []),
                    ...(catalogProfile.mpnRule
                      ? [{ label: 'MPN', value: catalogProfile.mpnRule }]
                      : []),
                    { label: 'Barcodes', value: catalogProfile.barcodeModelExplain },
                    { label: 'UPC', value: catalogProfile.upcLooksLike },
                    { label: 'EAN', value: catalogProfile.eanLooksLike },
                    { label: 'GTIN', value: catalogProfile.gtinLooksLike },
                    ...(catalogProfile.gs1Notes
                      ? [{ label: 'GS1', value: catalogProfile.gs1Notes }]
                      : []),
                    ...(catalogProfile.source
                      ? [{ label: 'Source', value: catalogProfile.source }]
                      : []),
                  ]}
                />
              </BvCard>
            )}

            <BvCard
              title="Brand assets"
              caption="Five slots cover every surface the mark appears on."
              action={
                <BvButton size="sm" endIcon={<IconArrowOut size={12} />} onClick={() => navigate(bvRoutes.logos(data.id))}>
                  Open logo library
                </BvButton>
              }
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px', p: '16px' }}>
                {(
                  [
                    { key: 'primary', label: 'Primary mark', filter: 'none', dark: false },
                    { key: 'wordmark', label: 'Wordmark', filter: 'none', dark: false },
                    { key: 'monochrome', label: 'Monochrome', filter: 'grayscale(1) contrast(1.4)', dark: false },
                    { key: 'dark', label: 'Dark background', filter: 'invert(1) grayscale(1) brightness(1.6)', dark: true },
                    { key: 'favicon', label: 'Favicon 64px', filter: 'none', dark: false },
                  ] as const
                ).map((variant) => {
                  const asset = data.logoAssets?.[variant.key];
                  const filled = Boolean(asset?.url);
                  return (
                    <Box
                      key={variant.key}
                      sx={{
                        border: `1px ${filled ? 'solid' : 'dashed'} ${filled ? bvColor.borderSubtle : bvColor.border}`,
                        borderRadius: `${bvRadius.control}px`,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          height: 58,
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: variant.dark ? bvColor.ink : bvColor.page,
                        }}
                      >
                        {filled ? (
                          <Box
                            component="img"
                            src={asset?.url}
                            alt=""
                            sx={{ maxWidth: 60, maxHeight: 34, objectFit: 'contain', filter: variant.filter }}
                          />
                        ) : (
                          <Box sx={{ color: bvColor.muted2, fontSize: 18, lineHeight: 1 }} aria-hidden>
                            +
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ bgcolor: bvColor.surface, px: '9px', py: '7px', borderTop: `1px solid ${bvColor.borderSubtle}` }}>
                        <Box sx={{ ...bvType.microCaption, fontWeight: 500, color: bvColor.ink }}>{variant.label}</Box>
                        <Box
                          sx={{
                            ...bvType.microCaption,
                            color: filled ? bvColor.success : bvColor.warning,
                            mt: '2px',
                          }}
                        >
                          {filled ? (asset?.format === 'vector' ? 'SVG' : 'Raster') : 'Empty'}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </BvCard>
          </Box>

          {/* right rail */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
            <TasksCard tasks={tasks} severity={severity} onRun={runTask} busy={runMutation.isPending} />

            <BvCard title="Ownership">
              <Box sx={{ p: '13px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
                  <Box
                    aria-hidden
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: `${bvRadius.xsmall}px`,
                      bgcolor: bvColor.ink,
                      color: bvColor.surface,
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {(data.ownership.organizationName || data.name).charAt(0).toUpperCase()}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink }}>
                      {data.ownership.organizationName || 'Unclaimed'}
                    </Box>
                    <Box sx={{ ...bvType.caption, color: bvColor.muted }}>
                      {data.ownership.contactName || 'No rep has an account yet'}
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '9px', mt: '13px' }}>
                  <BvButton
                    size="sm"
                    tone={facts.claimStatus === 'requested' ? 'primary' : 'default'}
                    onClick={() => {
                      if (facts.claimStatus === 'requested') {
                        navigate(bvRoutes.approvals(data.claimedBy.requestId));
                      } else if (facts.claimed) {
                        navigate(bvRoutes.organizations(data.claimedBy.organizationId || undefined));
                      } else {
                        openRepInvite();
                      }
                    }}
                  >
                    {facts.claimStatus === 'requested'
                      ? 'Review claim'
                      : facts.claimed
                        ? 'Open org'
                        : invitesEnabled
                          ? 'Invite rep'
                          : 'Copy signup link'}
                  </BvButton>
                  <BvButton size="sm" endIcon={<IconArrowOut size={12} />} onClick={() => navigate(bvRoutes.organizations())}>
                    Orgs
                  </BvButton>
                </Box>
                {unclaimed && invitesEnabled && invitations.length > 0 && (
                  <Box sx={{ mt: '13px', pt: '11px', borderTop: `1px solid ${bvColor.border}` }}>
                    <Eyebrow>Invitations</Eyebrow>
                    {invitations.map((invite) => (
                      <Box key={invite.id} sx={{ display: 'flex', alignItems: 'center', gap: '9px', mt: '8px' }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ ...bvType.tablePrimary, color: bvColor.ink, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {invite.email}
                          </Box>
                          <Box sx={{ ...bvType.caption, color: bvColor.muted }}>
                            {invite.status === 'accepted'
                              ? `Signed up ${formatFirestoreDateOnly(invite.acceptedAt)}; claim is in Approvals`
                              : invite.status === 'expired'
                                ? `Expired ${formatFirestoreDateOnly(invite.expiresAt)}`
                                : `Sent ${formatFirestoreDateOnly(invite.resentAt || invite.invitedAt)} · expires ${formatFirestoreDateOnly(invite.expiresAt)}`}
                          </Box>
                        </Box>
                        {invite.status === 'accepted' ? (
                          <BvButton size="xs" onClick={() => navigate(bvRoutes.approvals(data.claimedBy.requestId))}>
                            Approvals
                          </BvButton>
                        ) : (
                          <>
                            <BvButton
                              size="xs"
                              disabled={resendInviteMutation.isPending}
                              onClick={() => resendInviteMutation.mutate(invite)}
                            >
                              Resend
                            </BvButton>
                            <BvButton size="xs" tone="danger" onClick={() => setRevoking(invite)}>
                              Revoke
                            </BvButton>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </BvCard>

            <BvCard
              title="Brand dashboard"
              action={
                <BvButton size="sm" endIcon={<IconArrowOut size={12} />} onClick={() => navigate(bvRoutes.dashboard(data.id))}>
                  Open
                </BvButton>
              }
            >
              <Box sx={{ px: '13px', py: '11px', ...bvType.caption, color: bvColor.muted, lineHeight: 1.5 }}>
                {data.monitoring.priceMonitoring
                  ? 'Price monitoring is on — MAP violations and retailer compliance are tracked.'
                  : 'Price monitoring is off, so no MAP violations are being detected for this brand.'}
              </Box>
            </BvCard>
          </Box>
        </Box>
      </Box>

      <FormDialog
        open={inviteOpen}
        title={`Invite a rep from ${data.name}`}
        onClose={() => setInviteOpen(false)}
        onSubmit={() => sendInviteMutation.mutate()}
        submitLabel="Send invitation"
        submitDisabled={!inviteEmail.trim() || Boolean(validateEmail(inviteEmail))}
        loading={sendInviteMutation.isPending}
      >
        <Box sx={{ display: 'grid', gap: 2, pt: 1 }}>
          <Box sx={{ ...bvType.body, color: bvColor.muted }}>
            This emails them a link that opens vendor sign-up with {data.name} already chosen. When they sign up with this
            address, the claim in Approvals is marked as invited. You still approve it, and the link works once for 14 days.
          </Box>
          <ValidatedTextField
            autoFocus
            fullWidth
            label="Their work email"
            type="email"
            value={inviteEmail}
            validate={validateEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note (optional)"
            helperText="Shown in the email."
            value={inviteNote}
            inputProps={{ maxLength: 500 }}
            onChange={(event) => setInviteNote(event.target.value)}
          />
          {sendInviteMutation.error && <Alert severity="error">{(sendInviteMutation.error as Error).message}</Alert>}
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(revoking)}
        title="Revoke this invitation?"
        message={`The link emailed to ${revoking?.email || ''} will stop working. You can send a new invitation later.`}
        confirmLabel="Revoke"
        confirmColor="error"
        loading={revokeInviteMutation.isPending}
        onConfirm={() => revoking && revokeInviteMutation.mutate(revoking)}
        onClose={() => setRevoking(null)}
      />
    </Box>
  );
};

/** The right rail's task card. Border and header tint shift with severity. */
const TasksCard: React.FC<{
  tasks: BrandTask[];
  severity: ChipState | null;
  onRun: (task: BrandTask) => void;
  busy: boolean;
}> = ({ tasks, severity, onRun, busy }) => {
  const tint =
    severity === 'no'
      ? { border: bvColor.dangerBorder, bg: bvColor.dangerBg }
      : severity === 'part'
        ? { border: bvColor.warningBorder, bg: bvColor.warningBg }
        : { border: bvColor.border, bg: bvColor.page };

  return (
    <Box sx={{ ...bvCardSx, borderColor: tint.border }}>
      <Box
        sx={{
          px: '16px',
          py: '13px',
          bgcolor: tint.bg,
          borderBottom: `1px solid ${bvColor.borderSubtle}`,
        }}
      >
        <Box sx={{ ...bvType.cardTitle, color: bvColor.ink }}>
          Tasks
          {tasks.length > 0 && (
            <Box component="span" sx={{ color: bvColor.muted, ml: '6px', ...tabular }}>
              {tasks.length}
            </Box>
          )}
        </Box>
      </Box>
      {tasks.length === 0 ? (
        <Box sx={{ px: '13px', py: '14px', ...bvType.caption, color: bvColor.muted, lineHeight: 1.5 }}>
          Nothing outstanding — logo, catalog, ingestion and claim are all in place.
        </Box>
      ) : (
        tasks.map((task) => (
          <Box
            key={task.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              alignItems: 'center',
              gap: '10px',
              px: '13px',
              py: '11px',
              borderBottom: `1px solid ${bvColor.borderSubtle}`,
              '&:last-of-type': { borderBottom: 'none' },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ ...bvType.tableSecondary, fontWeight: 500, color: bvColor.ink }}>{task.label}</Box>
              <Box sx={{ ...bvType.microCaption, color: bvColor.muted, mt: '3px', lineHeight: 1.45 }}>{task.note}</Box>
            </Box>
            <BvButton size="xs" onClick={() => onRun(task)} disabled={busy}>
              {task.buttonLabel}
            </BvButton>
          </Box>
        ))
      )}
    </Box>
  );
};

export default BrandVendorsBrandPage;
